import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { STYLE, TEXT } from '../constants';
import { FlowContext, UploadContext } from '../pages';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { ILinkedAccount } from '../../../types';
import { useUploadMoneyTransferConfirmation } from '../../../hooks/mutations/useUploadMoneyTransferConfirmation';
import { useStore } from '../../../store';
import { takeSnapshot } from '../../../services/snapshot.service';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { ReactComponent as SuccessIcon } from '../../../assets/icons/svg/colored-icons/ic-colored-success-new.svg';
import Lottie from '../../../generic-components/Lottie';
import LottieAsset from '../../../assets/lotties/time_watch.json';

const {
  PAGES: { FLOW_SUCCEED },
} = STYLE;

const {
  PAGES: { FLOW_SUCCEED: TEXTS },
} = TEXT;

interface IProps {
  data: { date: string; amount: string; firstTimeMessage?: string };
  handleFinishFlow: () => void;
  activeLinkAccount?: ILinkedAccount;
}

export const PlaidFlowSucceed = ({ data, handleFinishFlow, activeLinkAccount }: IProps) => {
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.user);
  const [snapshotFormData, setSnapShotFormData] = useState<unknown>();
  const [isSnapshotActive, setIsSnapshotActive] = useState<boolean>(false);
  const { transactionId } = useContext(UploadContext);
  const { refetch: mutateSummary } = useAccountSummaryQuery();
  const { mutate: uploadTransferConfirmation, isLoading: isUploadSnapshot } = useUploadMoneyTransferConfirmation(
    {
      fileData: snapshotFormData,
      customerId,
      transactionId,
    },
    () => {
      setIsSnapshotActive(false);
    },
    () => {
      setIsSnapshotActive(false);
    },
  );
  const isAddMoneyFlow = useContext(FlowContext);
  const transferAmount = useMemo(() => {
    if (!data.amount.includes('.')) {
      return `$${data.amount}.00`;
    }
    return `$${data.amount}`;
  }, [data.amount]);

  const handleSnapshot = async () => {
    setIsSnapshotActive(true);
    const formData: unknown = await takeSnapshot();
    setIsSnapshotActive(false);

    setSnapShotFormData(formData);
  };

  useEffect(() => {
    trackMixPanel('View Plaid Result Screen', { Result: 'Success' });
    trackMixPanel('Add Money Success Screen', { Method: 'Plaid' });
    mutateSummary();
    setTimeout(() => {
      handleSnapshot();
    }, 1000);
  }, []);

  useEffect(() => {
    if (snapshotFormData && !isUploadSnapshot && !isSnapshotActive) {
      uploadTransferConfirmation();
    }
  }, [snapshotFormData]);
  const renderSubContent = () => {
    if (!isAddMoneyFlow) {
      return (
        <p className={FLOW_SUCCEED.SUB_CONTENT_1}>
          {`${t(TEXTS.SUB_CONTENT_LINE_1)} `}
          <span className={FLOW_SUCCEED.SPAN}>{`${t(TEXTS.SUB_CONTENT_LINE_2)} `}</span>
          {`${t(TEXTS.SUB_CONTENT_LINE_3)} `}
          <span className={FLOW_SUCCEED.SPAN}>{`${t(TEXTS.SUB_CONTENT_LINE_4)} ${data.date}.`}</span>
        </p>
      );
    }
    return <p className={FLOW_SUCCEED.NEW_ADD_MONEY_SUB_CONTENT}>{`${t(TEXTS.NEW_ADD_MONEY_SUB_CONTENT)}`}</p>;
  };
  const renderTransferDetails = () => (
    <div className='w-[376px] border-[1px] border-bw03 rounded-lg'>
      <div className='flex justify-between w-full h-[48px] items-center border-b-[1px] border-bw03 px-[16px]'>
        <div className='font-metricR text-md text-bw06'>{t(TEXTS.AMOUNT)}</div>
        <div className='font-metricRMedium text-xl text-action03'>{convertValueToUSD(JSON.parse(data.amount), 2)}</div>
      </div>
      {!isAddMoneyFlow && (
        <div className='flex justify-between w-auto mx-[16px] items-center border-b-[1px] border-bw03'>
          <div className='font-metricR text-md text-bw06 py-[15px] leading-4'>{t(TEXTS.FROM)}</div>
          <div className='font-metricRMedium text-md text-bw08 w-[55%] truncate text-right'>
            {isAddMoneyFlow
              ? `${activeLinkAccount?.accountHolderName ? `${activeLinkAccount.accountHolderName}, ` : ''}${activeLinkAccount?.institutionName}`
              : t(TEXTS.MY_LILI_ACCOUNT)}
          </div>
        </div>
      )}
      {!isAddMoneyFlow && (
        <div className='flex justify-between w-auto mx-[16px] items-center border-b-[1px] border-bw03'>
          <div className='font-metricR text-md text-bw06 py-[15px] leading-4'>{t(TEXTS.ACCOUNT_NUMBER)}</div>
          <div className='font-metricR text-sm text-bw08 w-[55%] truncate text-right'>
            {activeLinkAccount?.accountId}
          </div>
        </div>
      )}
      {!isAddMoneyFlow && (
        <div className='flex justify-between w-auto mx-[16px] items-center border-b-[1px] border-bw03'>
          <div className='font-metricR text-md text-bw06 py-[15px] leading-4'>{t(TEXTS.ROUTING_NUMBER)}</div>
          <div className='font-metricR text-sm text-bw08 w-[55%] truncate text-right'>
            {activeLinkAccount?.routingNumber}
          </div>
        </div>
      )}
      {!isAddMoneyFlow && (
        <div className='flex justify-between w-auto mx-[16px] items-center border-b-[1px] border-bw03'>
          <div className='font-metricR text-md text-bw06 py-[15px] leading-4'>{t(TEXTS.SEND_TO)}</div>
          <div className='font-metricR text-md text-bw08 w-[55%] truncate text-right'>
            {isAddMoneyFlow
              ? t(TEXTS.MY_LILI_ACCOUNT)
              : `${activeLinkAccount?.accountHolderName ? `${activeLinkAccount.accountHolderName}, ` : ''}${activeLinkAccount?.institutionName}`}
          </div>
        </div>
      )}
      <div
        className={`flex flex-col ${data.firstTimeMessage ? 'pb-[8px]' : ''} w-full px-[16px] items-center border-b-[1px] border-bw03`}
      >
        <div className='w-full flex items-center justify-between'>
          <div className='font-metricR text-md text-bw06 py-[15px] leading-4'>{t(TEXTS.ESTIMATED_ARRIVAL)}</div>
          <div className='font-metricR text-md text-bw08 w-[55%] truncate text-right'>{data.date}</div>
        </div>
        {data.firstTimeMessage && (
          <div className='py-[8px] w-full flex flex-col items-center justify-center rounded-[8px] bg-action00 mt-[6px]'>
            <Lottie lottieAsset={LottieAsset} isLoop={false} isAutoplay className='mb-[10px] w-[28px]' />
            <Heading
              title={data.firstTimeMessage}
              className='max-w-[276px] font-metricR text-[14px] text-bw08 text-center'
            />
          </div>
        )}
      </div>
      <div className='h-[48px] flex justify-center items-center bg-bw01 text-bw06 font-metricR text-sm rounded-b-lg'>
        {`${t(TEXTS.PROCESSING_DATE)} ${format(new Date(), 'MMM dd, yyyy')}`}
      </div>
    </div>
  );
  return (
    <div className={FLOW_SUCCEED.WRAPPER}>
      <SuccessIcon className='mb-[12px]' />
      <Heading
        testId='plaidSuccessTitle'
        title={isAddMoneyFlow ? t(TEXTS.HEADING) : `${transferAmount} ${t(TEXTS.HEADING_SEND)}`}
        className={isAddMoneyFlow ? FLOW_SUCCEED.HEADING_NEW : FLOW_SUCCEED.HEADING}
      />
      {!isAddMoneyFlow && renderSubContent()}
      {activeLinkAccount && data.date && data.amount && renderTransferDetails()}
      {!isAddMoneyFlow && <p className={FLOW_SUCCEED.SUB_CONTENT_2}>{t(TEXTS.SUB_CONTENT_LINE_5)}</p>}
      {isAddMoneyFlow && (
        <div className='font-sourceSansPro text-bw06 flex justify-center items-center text-[12px] box-border mb-[20px] mt-[30px]'>
          {t('NEW_ADD_MONEY_RIGHT_BOX')}
        </div>
      )}
      <Button
        testId='finishFlowCta'
        onClick={() => handleFinishFlow()}
        className={isAddMoneyFlow ? FLOW_SUCCEED.CTA_NEW : FLOW_SUCCEED.CTA}
        id='flow-succeed-cta'
        title={isAddMoneyFlow ? t(TEXTS.CTA_NEW) : t(TEXTS.CTA)}
      />
    </div>
  );
};
